import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Plus, Minus } from 'lucide-angular';
import { PhoneNumberPipe } from '../../../../shared/pipes/phone-number.pipe';

export interface Beneficiary {
  _id: string;
  contractId: string;
  userId: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    passportNumber: string;
    birthday: string;
    sex: string;
    phoneNumbers: any[];
  };
  eyoneExternalId: string;
  eyoneInternalId: string;
  beneficiaryExternalId: string;
  type: string;
  matricule: string;
  isActive: boolean;
  photo?: {
    presignedUrl: string;
  };
  isPrincipalInsured: boolean;
  medicalCare: {
    careLimit: number;
    carePercentage: number;
  };
  prestations: {
    label: string;
    code: string;
    pourcentage: number;
    medicalAct: boolean;
  }[];
  prestationTypes: Record<string, boolean>;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

@Component({
  selector: 'app-insurance-beneficiaries',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, PhoneNumberPipe],
  templateUrl: './insurance-beneficiaries.component.html',
})
export class InsuranceBeneficiariesComponent {
  private _beneficiaries: Beneficiary[] = [];
  @Input() set beneficiaries(value: Beneficiary[]) {
    this._beneficiaries = value;
    this.applySearchFilter(); // Appliquer le filtre quand les données changent
  }
  get beneficiaries(): Beneficiary[] {
    return this._beneficiaries;
  }

  @Input() loading = false;
  @Input() error: string | null = null;

  expandedIndex: number | null = null;
  readonly Plus = Plus;
  readonly Minus = Minus;

  // Propriétés pour la recherche
  searchTerm: string = '';
  filteredBeneficiaries: Beneficiary[] = [];

  // État pour contrôler l'affichage complet des prestations par bénéficiaire
  showAllPrestations: { [key: string]: boolean } = {};

  prestationList = [
    { label: 'Consultation' },
    { label: 'Prestation Pharmacie' },
    { label: 'Hospitalisation' },
    { label: 'Visite' },
    { label: 'Radiologie' },
    { label: 'Analyse' },
  ];

  /**
   * Méthode appelée lors de la saisie dans le champ de recherche
   */
  onSearchChange(): void {
    this.applySearchFilter();
  }

  /**
   * Applique le filtre de recherche sur les bénéficiaires
   */
  applySearchFilter(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredBeneficiaries = [...this._beneficiaries];
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredBeneficiaries = this._beneficiaries.filter((beneficiary) => {
        return (
          // Recherche par nom et prénom
          (beneficiary.userId.firstName &&
            beneficiary.userId.firstName.toLowerCase().includes(searchLower)) ||
          (beneficiary.userId.lastName &&
            beneficiary.userId.lastName.toLowerCase().includes(searchLower)) ||
          // Recherche par email
          (beneficiary.userId.email &&
            beneficiary.userId.email.toLowerCase().includes(searchLower)) ||
          // Recherche par numéro de passeport
          (beneficiary.userId.passportNumber &&
            beneficiary.userId.passportNumber
              .toLowerCase()
              .includes(searchLower)) ||
          // Recherche par matricule
          (beneficiary.matricule &&
            beneficiary.matricule.toLowerCase().includes(searchLower)) ||
          // Recherche par type de bénéficiaire
          (beneficiary.isPrincipalInsured &&
            'bénéficiaire principal'.includes(searchLower)) ||
          (!beneficiary.isPrincipalInsured &&
            'ayant droit'.includes(searchLower)) ||
          // Recherche par sexe
          (beneficiary.userId.sex &&
            beneficiary.userId.sex.toLowerCase().includes(searchLower)) ||
          // Recherche par date de naissance
          (beneficiary.userId.birthday &&
            beneficiary.userId.birthday.toLowerCase().includes(searchLower)) ||
          // Recherche par téléphone
          (beneficiary.userId.phoneNumbers &&
            beneficiary.userId.phoneNumbers.some(
              (phone) => phone.number && phone.number.includes(searchLower)
            )) ||
          // Recherche dans les prestations
          (beneficiary.prestations &&
            beneficiary.prestations.some(
              (prestation) =>
                prestation.label &&
                prestation.label.toLowerCase().includes(searchLower)
            ))
        );
      });
    }
  }

  /**
   * Retourne les prestations à afficher pour un bénéficiaire donné
   */
  getPrestationsToShow(beneficiary: Beneficiary): any[] {
    if (this.showAllPrestations[beneficiary._id]) {
      return beneficiary.prestations;
    }
    return beneficiary.prestations.filter(
      (prestation) => !prestation.medicalAct
    );
  }

  /**
   * Vérifie si le bénéficiaire a des prestations avec medicalAct = true
   */
  hasMedicalActPrestations(beneficiary: Beneficiary): boolean {
    return beneficiary.prestations.some((prestation) => prestation.medicalAct);
  }

  /**
   * Bascule l'affichage complet des prestations pour un bénéficiaire
   */
  toggleAllPrestations(beneficiaryId: string): void {
    this.showAllPrestations[beneficiaryId] =
      !this.showAllPrestations[beneficiaryId];
  }

  /**
   * Getter pour obtenir les bénéficiaires à afficher (filtrés ou tous)
   */
  get displayBeneficiaries(): Beneficiary[] {
    return this.filteredBeneficiaries.length > 0
      ? this.filteredBeneficiaries
      : this.beneficiaries;
  }
}
